import { useCasesConfig } from '@/modules/case/hooks/useCasesConfig';
import { CertificationReview } from '@/modules/certification/components/CertificationReview';
import { CertificationTest } from '@/modules/certification/components/CertificationTest';
import { useCertificationStatus } from '@/modules/certification/hooks/useCertificationStatus';

import { useCaseDetails } from '../hooks/useCaseDetails';

interface CertificationModalProps {
  caseId: string;
  userId: string;
  onClose: () => void;
}

/**
 * Router component that decides whether to show CertificationTest or CertificationReview
 * based on the user's certification status.
 *
 * Logic:
 * - If user has passed: Show CertificationReview (read-only mode with answers)
 * - Otherwise: Show CertificationTest (normal test mode)
 */
export const CertificationModal = ({ caseId, userId, onClose }: CertificationModalProps) => {
  const { status, loading } = useCertificationStatus(caseId, userId);
  const { getCaseMetadata, casesConfig } = useCasesConfig();
  const caseItem = casesConfig.cases.find((c) => c.id === caseId)!;

  const { details: caseDetails, loading: caseDetailsLoading } = useCaseDetails(caseItem.configUrl);

  // Show loading state while checking certification status
  if (loading || caseDetailsLoading || !caseDetails) {
    return null;
  }

  // If user has already passed, show review mode
  if (status.passed) {
    return (
      <CertificationReview
        caseId={caseId}
        caseTitle={caseDetails.displayName}
        userId={userId}
        onClose={onClose}
      />
    );
  }

  // Otherwise show normal test mode
  return (
    <CertificationTest
      caseId={caseId}
      userId={userId}
      onClose={onClose}
      onTestComplete={() => {
        // Trigger refresh of case cards to update certification button
        window.dispatchEvent(
          new CustomEvent('certification-updated', {
            detail: { caseId },
          })
        );
        onClose();
      }}
    />
  );
};
